import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { PageHeader } from "@/components/layout/PageHeader";
import { Countdown } from "@/components/common/Countdown";
import { Money } from "@/components/common/Money";
import { toast } from "sonner";
import {
  ArrowLeft,
  FileText,
  Shirt,
  Clock,
  CheckCircle,
  Info,
  Scissors,
} from "lucide-react";

interface QuoteLine {
  alterationType: string;
  unitPrice: number;
  qty: number;
  total: number;
  notes?: string | null;
}

interface QuoteItem {
  garmentType: string;
  tagCode?: string | null;
  qty: number;
  lines: QuoteLine[];
}

interface Quote {
  id: string;
  versionNo: number;
  status: string;
  subtotal: number;
  tax: number;
  deliveryFee: number;
  total: number;
  expiresAt: string | null;
  createdAt: string;
  items: QuoteItem[];
}

interface Order {
  id: string;
  publicId: string;
  status: string;
  orderType: string;
}

const statusConfig: Record<string, { color: string; label: string; icon: typeof CheckCircle }> = {
  VIEWED: { color: "bg-blue-100 text-blue-700", label: "Reviewing", icon: Clock },
  SENT: { color: "bg-amber-100 text-amber-700", label: "New", icon: FileText },
  APPROVED: { color: "bg-green-100 text-green-700", label: "Approved", icon: CheckCircle },
};

export default function CustomerQuotePage() {
  const navigate = useNavigate();
  const params = useParams();
  const orderId = params.id as string;

  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [order, setOrder] = useState<Order | null>(null);

  useEffect(() => {
    fetchQuote();
  }, [orderId]);

  async function fetchQuote() {
    try {
      const response = await fetch(`/api/customer/orders/${orderId}/quote`);

      if (!response.ok) {
        if (response.status === 404) {
          toast.error("No quote available for this order");
          navigate(`/customer/orders/${orderId}`);
          return;
        }
        throw new Error("Failed to fetch quote");
      }

      const data = await response.json();
      setQuote(data.quote);
      setOrder(data.order);
    } catch (error) {
      toast.error("Failed to load quote");
      navigate(`/customer/orders/${orderId}`);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Quote Details" />
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <Skeleton className="h-[300px]" />
            <Skeleton className="h-[200px]" />
          </div>
          <Skeleton className="h-[350px]" />
        </div>
      </div>
    );
  }

  if (!quote || !order) return null;

  const status = statusConfig[quote.status] || statusConfig.VIEWED;
  const StatusIcon = status.icon;
  const itemTotal = quote.items.reduce(
    (sum, item) => sum + item.lines.reduce((lineSum, line) => lineSum + line.total, 0),
    0
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Quote for ${order.publicId}`}
        actions={
          <Button
            variant="outline"
            onClick={() => navigate(`/customer/orders/${orderId}`)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Order
          </Button>
        }
      />

      <Alert className="border-blue-200 bg-blue-50">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertTitle className="text-blue-800">Quote Review</AlertTitle>
        <AlertDescription className="text-blue-700">
          This quote is ready for your review. Approval and payment functionality will be available in the next update.
        </AlertDescription>
      </Alert>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <FileText className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Quote v{quote.versionNo}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {order.orderType === "ALTERATION" ? "Alteration" : "Stitching"} Services
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={status.color}>
                    <StatusIcon className="h-3 w-3 mr-1" />
                    {status.label}
                  </Badge>
                  {quote.expiresAt && (
                    <Countdown
                      targetDate={quote.expiresAt}
                      label="Expires:"
                      onExpired={() => {
                        toast.error("Quote has expired");
                        navigate(`/customer/orders/${orderId}`);
                      }}
                    />
                  )}
                </div>
              </div>
            </CardHeader>
          </Card>

          {quote.items.map((item, itemIndex) => (
            <Card key={itemIndex}>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Shirt className="h-4 w-4 text-muted-foreground" />
                  <CardTitle className="text-base">{item.garmentType}</CardTitle>
                  {item.tagCode && (
                    <Badge variant="outline" className="text-xs">
                      Tag: {item.tagCode}
                    </Badge>
                  )}
                  <span className="ml-auto text-sm text-muted-foreground">
                    Qty: {item.qty}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {item.lines.map((line, lineIndex) => (
                    <div
                      key={lineIndex}
                      className="flex items-start justify-between gap-4 p-3 bg-muted/50 rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Scissors className="h-4 w-4 text-muted-foreground" />
                          <p className="font-medium">{line.alterationType}</p>
                        </div>
                        {line.notes && (
                          <p className="text-sm text-muted-foreground mt-1 ml-6">
                            {line.notes}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">
                          <Money amount={line.unitPrice} /> x {line.qty}
                        </p>
                        <p className="font-semibold">
                          <Money amount={line.total} />
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="space-y-6">
          <Card className="sticky top-6">
            <CardHeader>
              <CardTitle className="text-base">Quote Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Services Total</span>
                  <Money amount={quote.subtotal} />
                </div>
                {quote.tax > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Tax</span>
                    <Money amount={quote.tax} />
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Delivery Fee</span>
                  <Money amount={quote.deliveryFee} />
                </div>
                <Separator />
                <div className="flex justify-between font-semibold text-lg">
                  <span>Total</span>
                  <Money amount={quote.total} className="text-primary" />
                </div>
              </div>

              <Separator />

              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Next Steps</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Once you&apos;re happy with the quote, you&apos;ll be able to approve and make payment. This feature is coming soon!
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate(`/customer/orders/${orderId}`)}
                >
                  View Order Details
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
